import { setupLayouts } from 'virtual:meta-layouts'
import generatedRoutes from 'virtual:generated-pages'
import type { RouteRecordRaw } from 'vue-router'
import type { Route } from '@/types/global'
import useSettingsStore from '@/store/modules/settings'
import { componentsMap } from '@/vendor/componentsMap'

// AI-GEN-BEGIN - 扩展路由类型，支持业务自定义字段
type AppRouteRecordRaw = RouteRecordRaw & {
  id?: string
  parentId?: string
  enabled?: string
  hidden?: boolean
  children?: AppRouteRecordRaw[]
}
// AI-GEN-END

function Layout() {
  return import('@/layouts/index.vue')
}

/**
 * @description 固定路由（默认路由）
 */
const constantRoutes: AppRouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/loginIndex.vue'),
    meta: {
      title: '登录',
      hidden: true,
    },
  },
  {
    path: '/',
    component: Layout,
    meta: {
      title: '首页',
      icon: 'icon-home-solid',
      breadcrumb: false,
      hidden: false,
    },
    redirect: '/data-middle-office',
    children: [
      {
        path: 'data-middle-office',
        component: () => import('@/views/dashboard/index.vue'),
        name: 'DataMiddleOffice',
        meta: {
          title: '首页',
          icon: '',
          affix: true,
          hidden: false,
        },
      },
      {
        path: 'finacial-module/business-analysis/lenovo-mail-send/mail-send-list',
        component: componentsMap.lenovoMailSendList,
        name: 'LenovoMailSendList',
        meta: {
          title: '发送记录列表',
          icon: 'icon-document-solid',
          hidden: true,
        },
      },
      // AI-GEN-BEGIN - 联想邮件-收件人信息维护
      {
        path: 'finacial-module/business-analysis/lenovo-mail-send/mail-recipient-groups',
        component: componentsMap.lenovoMailRecipientGroupList,
        name: 'LenovoMailRecipientGroupList',
        meta: {
          title: '收件人信息维护',
          icon: 'icon-document-solid',
          hidden: false,
        },
      },
      {
        path: 'finacial-module/business-analysis/lenovo-mail-send/mail-recipient-groups/:groupId/recipients',
        component: componentsMap.lenovoMailRecipientGroupRecipients,
        name: 'LenovoMailRecipientGroupRecipients',
        meta: {
          title: '收件人明细',
          icon: 'icon-document-solid',
          hidden: true,
        },
      },
      // AI-GEN-END
      {
        path: 'demo',
        name: 'Demo',
        component: () => import('@/views/demo/index.vue'),
        meta: {
          title: 'demo',
          hidden: true,
        },
        children: [
          {
            path: 'former-demo',
            component: () => import('@/views/demo/components/formerDemo.vue'),
            name: 'FormerDemo',
            meta: { title: 'former-demo', icon: 'icon-home-solid', affix: true },
          },
          {
            path: 'form-design',
            component: () => import('@/views/formDesign/index.vue'),
            name: 'FormDesign',
            meta: { title: 'form-design', icon: 'icon-home-solid', affix: true },
          },
          {
            path: 'demo-navigator-location',
            component: () => import('@/views/demo/components/navigatorLocationComp.vue'),
            name: 'NavigatorLocationComp',
            meta: { title: 'navigator-location', icon: 'icon-home-solid', affix: true },
          },
          {
            path: 'g2n-comp',
            component: () => import('@/views/demo/components/g2nComp.vue'),
            name: 'G2nComp',
            meta: { title: 'g2n-comp', icon: 'icon-home-solid', affix: true },
          },
          {
            path: 'config-budget-page-comp',
            component: () => import('@/views/demo/components/configBudgetPageComp.vue'),
            name: 'ConfigBudgetPageComp',
            meta: { title: 'config-budget-page-comp', icon: 'icon-home-solid', affix: true },
          },
          {
            path: 'create-promotion-voucher-rule',
            component: () => import('@/views/demo/promotionVoucher/promotionVoucherRule/components/createPromotionVoucherRule.vue'),
            name: 'CreatePromotionVoucherRule',
            meta: { title: 'create-promotion-voucher-rule', icon: 'icon-home-solid', affix: true },
          },
        ],
      },
      // AI-GEN-BEGIN - 场景配置（保证 /bank-flow/bank-scene-config 在 backend/frontend 模式下可访问）
      {
        path: 'bank-flow/bank-scene-config',
        component: () => import('@/views/bankFlow/bankSceneConfig/index.vue'),
        name: 'BankSceneConfigIndex',
        meta: { title: '场景配置', icon: 'icon-document-solid', hidden: true },
      },
      {
        path: 'bank-flow/bank-scene-config/create',
        component: () => import('@/views/bankFlow/bankSceneConfig/create.vue'),
        name: 'BankSceneConfigCreate',
        meta: {
          title: '新增场景配置',
          icon: 'icon-document-solid',
          hidden: true,
          // AI-GEN-BEGIN - 子页高亮左侧列表菜单（与 SubSidebar el-menu default-active 一致）
          activeMenu: '/bank-flow/bank-scene-config',
          // AI-GEN-END
        },
      },
      {
        path: 'bank-flow/bank-scene-config/:id/edit',
        component: () => import('@/views/bankFlow/bankSceneConfig/[id]/edit.vue'),
        name: 'BankSceneConfigEdit',
        meta: {
          title: '编辑场景配置',
          icon: 'icon-document-solid',
          hidden: true,
          activeMenu: '/bank-flow/bank-scene-config',
        },
      },
      {
        path: 'bank-flow/alipay-scene-config',
        component: () => import('@/views/bankFlow/bankSceneConfig/index.vue'),
        name: 'PaymentSceneConfigUpdateIndex',
        meta: {
          title: '支付场景配置更新',
          icon: 'icon-document-solid',
          hidden: true,
          fixedBankCode: 'tmall_alipay',
        },
      },
      {
        path: 'bank-flow/alipay-scene-config/create',
        component: () => import('@/views/bankFlow/bankSceneConfig/create.vue'),
        name: 'PaymentSceneConfigUpdateCreate',
        meta: {
          title: '新增支付场景配置',
          icon: 'icon-document-solid',
          hidden: true,
          fixedBankCode: 'tmall_alipay',
          activeMenu: '/bank-flow/alipay-scene-config',
        },
      },
      {
        path: 'bank-flow/alipay-scene-config/:id/edit',
        component: () => import('@/views/bankFlow/bankSceneConfig/[id]/edit.vue'),
        name: 'PaymentSceneConfigUpdateEdit',
        meta: {
          title: '编辑支付场景配置',
          icon: 'icon-document-solid',
          hidden: true,
          fixedBankCode: 'tmall_alipay',
          activeMenu: '/bank-flow/alipay-scene-config',
        },
      },
      // AI-GEN-END
    ],
  },
  {
    id: '5',
    parentId: '',
    component: () => import('@/layouts/personalInfoLayout.vue'),
    path: '/personal-info',
    enabled: '1',
    redirect: '/personal-info/profile-manage-index',
    name: 'PersonalInfoLayout',
    meta: {
      hidden: true,
      title: '个人设置',
      icon: 'icon-lock',
    },
    children: [
      {
        component: () => import('@/views/profileManage/profileManageIndex.vue'),
        path: 'profile-manage-index',
        name: 'ProfileManageIndex',
        meta: {
          hidden: true,
          title: '用户资料',
          icon: 'icon-document-solid',
          roles: ['admin'],
        },
      },
      {
        component: () => import('@/views/profileManage/changePwdComp.vue'),
        path: 'change-pwd',
        name: 'ChangePwd',
        meta: { title: '修改密码' },
      },
      {
        component: () => import('@/views/profileManage/aiKeyComp.vue'),
        path: 'ai-key',
        name: 'AiKey',
        meta: { title: '绑定ChatGPT' },
      },
    ],
  },
  {
    id: '4',
    parentId: '',
    component: () => import('@/layouts/loginedLayout.vue'),
    path: '/account-config',
    enabled: '1',
    redirect: '/account-config/force-change-pwd',
    name: 'AccountConfig',
    meta: {
      hidden: true,
      title: '修改密码页',
      icon: 'icon-lock',
    },
    children: [
      {
        id: '41',
        parentId: '4',
        component: () => import('@/views/changePwd/forceChangePwdComp.vue'),
        enabled: '1',
        hidden: false,
        path: 'force-change-pwd',
        name: 'ForceChangePwdComp',
        meta: { title: '修改密码页' },
      },
      {
        id: '42',
        parentId: '4',
        component: () => import('@/views/resetPwd/resetPwdComp.vue'),
        enabled: '1',
        hidden: false,
        path: 'reset-pwd',
        name: 'ResetPwdComp',
        meta: { title: '重置密码页' },
      },
      {
        id: '43',
        parentId: '4',
        component: () => import('@/views/resetPwd/SSOLoginRedirectURLComp.vue'),
        enabled: '1',
        hidden: false,
        path: 'sso-login',
        name: 'SSOLoginRedirectURLComp',
        meta: { title: 'SSO重定向' },
      },
    ],
  },
  /* 404 */
  {
    path: '/:all(.*)*',
    name: 'notFound',
    component: () => import('@/views/[...all].vue'),
    meta: {
      title: '找不到页面',
      hidden: true,
    },
  },
]

/**
 * @description 系统路由
 */
const systemRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/index.vue'),
    meta: {
      title: () => useSettingsStore().settings.home.title,
      breadcrumb: false,
    },
    children: [
      {
        path: 'reload',
        name: 'reload',
        component: () => import('@/views/reload.vue'),
        meta: {
          title: '重新加载',
          breadcrumb: false,
        },
      },
      {
        path: 'setting',
        name: 'personalSetting',
        component: () => import('@/views/personal/setting.vue'),
        meta: {
          title: '个人设置',
          cache: 'personalEditPassword',
        },
      },
      {
        path: 'edit/password',
        name: 'personalEditPassword',
        component: () => import('@/views/personal/edit.password.vue'),
        meta: {
          title: '修改密码',
        },
      },
    ],
  },
]

/** 前端路由：异步路由（异步路由、导航栏路由） */
const asyncRoutes: Route.recordMainRaw[] = []

const constantRoutesByFilesystem = generatedRoutes.filter((item) => {
  return item.meta?.enabled !== false && item.meta?.constant === true
})

const asyncRoutesByFilesystem = setupLayouts(generatedRoutes.filter((item) => {
  return item.meta?.enabled !== false && item.meta?.constant !== true && item.meta?.layout !== false
}))

export {
  constantRoutes,
  systemRoutes,
  asyncRoutes,
  constantRoutesByFilesystem,
  asyncRoutesByFilesystem,
}
